import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add,
  Cancel,
  Delete,
  Edit,
  RadioButtonChecked,
  RadioButtonUnchecked,
  Shuffle,
} from "@mui/icons-material";
import { useMutation, useQuery } from "@apollo/client";
import { Trans, useTranslation } from "react-i18next";
import {
  GET_MATCHING_CONFIGS,
  GET_MATCHING_RUNS,
  CREATE_MATCHING_CONFIG,
  UPDATE_MATCHING_CONFIG,
  DELETE_MATCHING_CONFIG,
  CREATE_MATCHING_RUN,
  LAUNCH_MATCHING_SURVEY,
  RANDOMIZE_MATCHING_RUN,
  MatchingConfigsResult,
} from "../../Queries";
import { MatchingConfig, MatchingRun, MatchingRunStatus } from "../../types";
import { MatchingConfigEditor, DraftConfig } from "./MatchingConfigEditor";

const STEP_KEYS = ["stepSelect", "stepConfigure", "stepReview"] as const;

// Which run statuses mean the survey is already live (→ show settings view)
const LIVE_STATUSES = new Set([
  MatchingRunStatus.SurveyActive,
  MatchingRunStatus.Processing,
  MatchingRunStatus.Ready,
  MatchingRunStatus.Applied,
  MatchingRunStatus.LmsSynced,
]);

type SizeMode = "size" | "count";

interface RunDraft {
  configId: string;
  sizeMode: SizeMode;
  teamSize: number;
  numTeams: string;
  surveyDueDate: string;
  reminderEnabled: boolean;
  notes: string;
}

function defaultRunDraft(configId = ""): RunDraft {
  return {
    configId,
    sizeMode: "size",
    teamSize: 4,
    numTeams: "",
    surveyDueDate: "",
    reminderEnabled: false,
    notes: "",
  };
}

interface Props {
  open: boolean;
  courseId: string;
  existingRun?: MatchingRun;
  onClose: () => void;
  onLaunched: (run: MatchingRun) => void;
  /** Called when instructor cancels (hard-deletes) the run from the settings overlay. */
  onCancelRun?: () => void;
}

export function MatchingWizard({
  open,
  courseId,
  existingRun,
  onClose,
  onLaunched,
  onCancelRun,
}: Props) {
  const [t] = useTranslation();
  const [step, setStep] = useState(0);
  const [selectedConfigId, setSelectedConfigId] = useState<string>(
    existingRun?.configId ?? "",
  );
  const [runDraft, setRunDraft] = useState<RunDraft>(
    defaultRunDraft(existingRun?.configId ?? ""),
  );
  const [configEditorOpen, setConfigEditorOpen] = useState(false);
  // When editing an existing config, hold the config object here
  const [editingConfig, setEditingConfig] = useState<
    MatchingConfig | undefined
  >(undefined);
  const [launchError, setLaunchError] = useState("");

  // If the run is already live, show a settings view instead of the wizard
  const isSettingsView = !!existingRun && LIVE_STATUSES.has(existingRun.status);

  // Statuses where cancel is allowed (not yet applied to course groups)
  const isCancelableRun =
    existingRun &&
    new Set([
      MatchingRunStatus.SurveyActive,
      MatchingRunStatus.Processing,
      MatchingRunStatus.Ready,
    ]).has(existingRun.status);

  React.useEffect(() => {
    if (open) {
      setStep(0);
      setLaunchError("");
      setSelectedConfigId(existingRun?.configId ?? "");
      setRunDraft(defaultRunDraft(existingRun?.configId ?? ""));
    }
  }, [open, existingRun]);

  const { data: configsData, refetch: refetchConfigs } =
    useQuery<MatchingConfigsResult>(GET_MATCHING_CONFIGS, { skip: !open });
  const allConfigs = configsData?.matchingConfigs ?? [];

  // Only show template configs in the list; also show the just-selected non-template (current session)
  const visibleConfigs = allConfigs.filter(
    (c) => c.isTemplate || c.id === selectedConfigId,
  );

  const [createConfig, { loading: creatingConfig }] = useMutation(
    CREATE_MATCHING_CONFIG,
    { refetchQueries: ["GetMatchingConfigs"] },
  );

  const [updateConfig, { loading: updatingConfig }] = useMutation(
    UPDATE_MATCHING_CONFIG,
    { refetchQueries: ["GetMatchingConfigs"] },
  );

  const [deleteConfig] = useMutation(DELETE_MATCHING_CONFIG, {
    refetchQueries: ["GetMatchingConfigs"],
  });

  const [createRun, { loading: creatingRun }] = useMutation(
    CREATE_MATCHING_RUN,
    {
      refetchQueries: [{ query: GET_MATCHING_RUNS, variables: { courseId } }],
    },
  );

  const [launchSurvey, { loading: launching }] = useMutation(
    LAUNCH_MATCHING_SURVEY,
    {
      refetchQueries: [{ query: GET_MATCHING_RUNS, variables: { courseId } }],
    },
  );

  const [randomizeMatchingRun, { loading: randomizing }] = useMutation(
    RANDOMIZE_MATCHING_RUN,
    {
      refetchQueries: [{ query: GET_MATCHING_RUNS, variables: { courseId } }],
    },
  );

  const selectedConfig = allConfigs.find((c) => c.id === selectedConfigId);

  const configInput = (draft: DraftConfig) => ({
    name: draft.name,
    description: draft.description,
    isTemplate: draft.isTemplate,
    timePreferenceWeight: 0.25,
    rolePreferenceWeight: 0.25,
    personalityWeight: 0.25,
    preferredTeammatesWeight: 0.25,
    similarityPreference: 0.0,
    questions: draft.questions.map((q, i) => ({
      questionType: q.questionType,
      label: q.label,
      description: q.description ?? "",
      weight: Math.max(1, 10 - i),
      order: i,
      options: q.options ?? [],
      required: true,
      similarityPreference: q.similarityPreference ?? null,
    })),
  });

  async function handleSaveConfig(draft: DraftConfig) {
    if (editingConfig) {
      // Update existing config
      await updateConfig({
        variables: { id: editingConfig.id, input: configInput(draft) },
      });
    } else {
      // Create new config
      const result = await createConfig({
        variables: { input: configInput(draft) },
      });
      const newConfig = result.data?.createMatchingConfig;
      if (newConfig?.id) {
        setSelectedConfigId(newConfig.id);
        setRunDraft((d) => ({ ...d, configId: newConfig.id }));
      }
    }
    setEditingConfig(undefined);
    setConfigEditorOpen(false);
  }

  async function handleDeleteConfig(configId: string, e: React.MouseEvent) {
    e.stopPropagation();
    await deleteConfig({ variables: { id: configId } });
    if (selectedConfigId === configId) {
      setSelectedConfigId("");
      setRunDraft((d) => ({ ...d, configId: "" }));
    }
    refetchConfigs();
  }

  async function handleLaunch() {
    setLaunchError("");
    try {
      const runResult = await createRun({
        variables: {
          input: {
            configId: selectedConfigId,
            // Always send a valid int — default to 4 if count mode
            teamSize: runDraft.sizeMode === "size" ? runDraft.teamSize : 4,
            numTeams:
              runDraft.sizeMode === "count" && runDraft.numTeams
                ? parseInt(runDraft.numTeams, 10)
                : null,
            surveyDueDate: runDraft.surveyDueDate || null,
            reminderEnabled: runDraft.reminderEnabled,
            reminderFrequency: "",
            notes: runDraft.notes,
          },
        },
      });

      const run = runResult.data?.createMatchingRun;
      if (!run?.id) return;

      const launchResult = await launchSurvey({ variables: { runId: run.id } });
      const launchedRun = launchResult.data?.launchMatchingSurvey;
      if (launchedRun) {
        onLaunched(launchedRun);
        onClose();
      }
    } catch (err: unknown) {
      setLaunchError(
        err instanceof Error
          ? err.message
          : t("modals.matchingWizard.unexpectedError"),
      );
    }
  }

  async function handleRandomize() {
    const result = await randomizeMatchingRun({
      variables: {
        input: {
          configId: selectedConfigId || null,
          teamSize: runDraft.sizeMode === "size" ? runDraft.teamSize : 4,
          numTeams:
            runDraft.sizeMode === "count" && runDraft.numTeams
              ? parseInt(runDraft.numTeams, 10)
              : null,
          notes: runDraft.notes,
          surveyDueDate: null,
          reminderEnabled: false,
          reminderFrequency: "",
        },
      },
    });
    const run = result?.data?.randomizeMatchingRun;
    if (run?.id) {
      onLaunched(run);
      onClose();
    }
  }

  const setField = <K extends keyof RunDraft>(key: K, value: RunDraft[K]) =>
    setRunDraft((d) => ({ ...d, [key]: value }));

  const canProceed = [
    selectedConfigId !== "",
    runDraft.sizeMode === "size"
      ? runDraft.teamSize >= 2
      : runDraft.numTeams !== "" && parseInt(runDraft.numTeams, 10) >= 1,
    true,
  ];

  // ── Settings view (run already live) ────────────────────────────────────────

  if (isSettingsView && existingRun) {
    const run = existingRun;
    const config = allConfigs.find((c) => c.id === run.configId);
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>{t("modals.matchingWizard.settingsTitle")}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={0}>
            {[
              [
                t("modals.matchingWizard.labelConfiguration"),
                config?.name ?? run.configId,
              ],
              [
                t("modals.matchingWizard.labelTeamStructure"),
                run.numTeams
                  ? t("modals.matchingWizard.teamsCount", {
                      count: run.numTeams,
                    })
                  : t("modals.matchingWizard.studentsPerTeam", {
                      count: run.teamSize,
                    }),
              ],
              [
                t("modals.matchingWizard.labelSurveyDue"),
                run.surveyDueDate
                  ? new Date(run.surveyDueDate).toLocaleString()
                  : t("modals.matchingWizard.noDeadline"),
              ],
              [
                t("modals.matchingWizard.labelReminderEmails"),
                run.reminderEnabled
                  ? t("modals.matchingWizard.enabled")
                  : t("modals.matchingWizard.disabled"),
              ],
            ].map(([label, value]) => (
              <Box key={label as string} mb={1.5}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  {label}
                </Typography>
                <Typography variant="body2">{value}</Typography>
              </Box>
            ))}
            {run.notes && (
              <Box mb={1.5}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  {t("modals.matchingWizard.messageToStudents")}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    p: 1.5,
                    bgcolor: "action.hover",
                    borderRadius: 1,
                    fontStyle: "italic",
                  }}
                >
                  &ldquo;{run.notes}&rdquo;
                </Typography>
              </Box>
            )}
            {config && config.questions && config.questions.length > 0 && (
              <>
                <Divider sx={{ my: 1 }} />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={600}
                  display="block"
                  mb={1}
                >
                  {t("modals.matchingWizard.surveyQuestions")}
                </Typography>
                {config.questions.map((q) => (
                  <Box
                    key={q.id}
                    display="flex"
                    justifyContent="space-between"
                    py={0.5}
                  >
                    <Typography variant="body2">{q.label}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t("modals.matchingWizard.weightLabel", {
                        weight: q.weight,
                      })}
                    </Typography>
                  </Box>
                ))}
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          {isCancelableRun && onCancelRun && (
            <Button
              color="error"
              variant="outlined"
              startIcon={<Cancel />}
              aria-label={t("modals.matchingWizard.cancelMatchingAria")}
              onClick={() => {
                onCancelRun();
                onClose();
              }}
            >
              {t("modals.matchingWizard.cancelMatching")}
            </Button>
          )}
          <Button onClick={onClose}>{t("modals.matchingWizard.close")}</Button>
        </DialogActions>
      </Dialog>
    );
  }

  // ── Wizard ───────────────────────────────────────────────────────────────────

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>{t("modals.matchingWizard.wizardTitle")}</DialogTitle>
        <DialogContent dividers>
          <Stepper activeStep={step} sx={{ mb: 3 }}>
            {STEP_KEYS.map((key) => (
              <Step key={key}>
                <StepLabel>{t(`modals.matchingWizard.${key}`)}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* ── Step 0 — Select Config ── */}
          {step === 0 && (
            <Box>
              <Typography variant="body2" color="text.secondary" mb={2}>
                {t("modals.matchingWizard.step0Intro")}
              </Typography>

              {visibleConfigs.length > 0 ? (
                <List
                  disablePadding
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    mb: 2,
                  }}
                >
                  {visibleConfigs.map((c: MatchingConfig, idx) => (
                    <ListItem
                      key={c.id}
                      disablePadding
                      divider={idx < visibleConfigs.length - 1}
                      secondaryAction={
                        <Box display="flex" gap={0.5}>
                          <Tooltip
                            title={t("modals.matchingWizard.editConfigTooltip")}
                          >
                            <IconButton
                              size="small"
                              aria-label={t(
                                "modals.matchingWizard.editConfigAria",
                                { name: c.name },
                              )}
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingConfig(c);
                                setConfigEditorOpen(true);
                              }}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip
                            title={t(
                              "modals.matchingWizard.deleteConfigTooltip",
                            )}
                          >
                            <IconButton
                              size="small"
                              color="error"
                              aria-label={t(
                                "modals.matchingWizard.deleteConfigAria",
                                { name: c.name },
                              )}
                              onClick={(e) => handleDeleteConfig(c.id, e)}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      }
                    >
                      <ListItemButton
                        selected={selectedConfigId === c.id}
                        onClick={() => {
                          setSelectedConfigId(c.id);
                          setRunDraft((d) => ({ ...d, configId: c.id }));
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          {selectedConfigId === c.id ? (
                            <RadioButtonChecked
                              color="primary"
                              fontSize="small"
                            />
                          ) : (
                            <RadioButtonUnchecked fontSize="small" />
                          )}
                        </ListItemIcon>
                        <ListItemText
                          primary={c.name}
                          secondary={
                            c.description ||
                            t("modals.matchingWizard.questionCount", {
                              count: c.questions.length,
                            })
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography color="text.secondary" variant="body2" mb={2}>
                  {t("modals.matchingWizard.noTemplates")}
                </Typography>
              )}

              <Box display="flex" gap={1} mt={1} flexWrap="wrap">
                <Button
                  variant="text"
                  size="small"
                  color="light"
                  startIcon={<Add />}
                  onClick={() => {
                    setEditingConfig(undefined);
                    setConfigEditorOpen(true);
                  }}
                >
                  {t("modals.matchingWizard.newPreferences")}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  color="light"
                  startIcon={<Shuffle />}
                  disabled={randomizing}
                  onClick={handleRandomize}
                >
                  {randomizing
                    ? t("modals.matchingWizard.randomizing")
                    : t("modals.matchingWizard.randomizeTeams")}
                </Button>
              </Box>
            </Box>
          )}

          {/* ── Step 1 — Run Config ── */}
          {step === 1 && (
            <Stack spacing={2.5}>
              {/* Team structure: toggle + compact input side-by-side */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  {t("modals.matchingWizard.defineTeamStructure")}
                </Typography>
                {/* Center-align so the toggle text and input value sit at the
                    same visual height. The MUI floating label adds ~14 px of
                    overhead above the actual input box; without centering the
                    toggle buttons end up floating too high. */}
                <Box
                  display="flex"
                  alignItems="center"
                  gap={2}
                  flexWrap="wrap"
                  mt={1}
                >
                  <ToggleButtonGroup
                    value={runDraft.sizeMode}
                    exclusive
                    onChange={(_e, v) => {
                      if (v) setField("sizeMode", v as SizeMode);
                    }}
                    size="small"
                  >
                    <ToggleButton value="size" sx={{ textTransform: "none" }}>
                      {t("modals.matchingWizard.teamSizeToggle")}
                    </ToggleButton>
                    <ToggleButton value="count" sx={{ textTransform: "none" }}>
                      {t("modals.matchingWizard.teamCountToggle")}
                    </ToggleButton>
                  </ToggleButtonGroup>

                  {runDraft.sizeMode === "size" ? (
                    <TextField
                      label={t("modals.matchingWizard.studentsPerTeamLabel")}
                      type="number"
                      value={runDraft.teamSize}
                      onChange={(e) =>
                        setField("teamSize", parseInt(e.target.value, 10) || 4)
                      }
                      inputProps={{ min: 2, max: 20 }}
                      InputLabelProps={{ shrink: true }}
                      size="small"
                      sx={{ width: 120 }}
                    />
                  ) : (
                    <TextField
                      label={t("modals.matchingWizard.numberOfTeamsLabel")}
                      type="number"
                      value={runDraft.numTeams}
                      onChange={(e) => setField("numTeams", e.target.value)}
                      inputProps={{ min: 1 }}
                      InputLabelProps={{ shrink: true }}
                      size="small"
                      sx={{ width: 120 }}
                    />
                  )}
                </Box>
              </Box>

              {/* Due date */}
              <Box>
                <TextField
                  label={t("modals.matchingWizard.surveyDueDateLabel")}
                  type="datetime-local"
                  size="small"
                  value={runDraft.surveyDueDate}
                  onChange={(e) => setField("surveyDueDate", e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    minWidth: 180,
                    "& input": { colorScheme: "dark" },
                  }}
                />
              </Box>

              {/* Reminder emails */}
              <FormControlLabel
                control={
                  <Switch
                    checked={runDraft.reminderEnabled}
                    onChange={(e) =>
                      setField("reminderEnabled", e.target.checked)
                    }
                  />
                }
                label={t("modals.matchingWizard.reminderSwitchLabel")}
              />

              {/* Notes */}
              <TextField
                label={t("modals.matchingWizard.messageLabel")}
                fullWidth
                multiline
                rows={2}
                value={runDraft.notes}
                onChange={(e) => setField("notes", e.target.value)}
                helperText={t("modals.matchingWizard.messageHelper")}
              />
            </Stack>
          )}

          {/* ── Step 2 — Review ── */}
          {step === 2 && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                {t("modals.matchingWizard.reviewHeading")}
              </Typography>
              {[
                [
                  t("modals.matchingWizard.labelConfiguration"),
                  selectedConfig?.name ?? selectedConfigId,
                ],
                [
                  t("modals.matchingWizard.labelTeamStructure"),
                  runDraft.sizeMode === "size"
                    ? t("modals.matchingWizard.studentsPerTeam", {
                        count: runDraft.teamSize,
                      })
                    : t("modals.matchingWizard.teamsCount", {
                        count: parseInt(runDraft.numTeams, 10) || 0,
                      }),
                ],
                ...(runDraft.surveyDueDate
                  ? [
                      [
                        t("modals.matchingWizard.surveyCloses"),
                        new Date(runDraft.surveyDueDate).toLocaleString(),
                      ],
                    ]
                  : []),
                [
                  t("modals.matchingWizard.reminders"),
                  runDraft.reminderEnabled
                    ? t("modals.matchingWizard.enabled")
                    : t("modals.matchingWizard.disabled"),
                ],
              ].map(([label, value]) => (
                <Box key={label as string} mb={1}>
                  <Typography variant="caption" color="text.secondary">
                    {label}
                  </Typography>
                  <Typography variant="body2">{value as string}</Typography>
                </Box>
              ))}

              {runDraft.notes && (
                <Box mb={1}>
                  <Typography variant="caption" color="text.secondary">
                    {t("modals.matchingWizard.messageToStudents")}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      p: 1,
                      bgcolor: "action.hover",
                      borderRadius: 1,
                      fontStyle: "italic",
                      mt: 0.25,
                    }}
                  >
                    &ldquo;{runDraft.notes}&rdquo;
                  </Typography>
                </Box>
              )}

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  mt: 2,
                  p: 1.5,
                  bgcolor: "action.hover",
                  borderRadius: 1,
                  borderLeft: "3px solid",
                  borderColor: "primary.main",
                }}
              >
                <Trans
                  i18nKey="modals.matchingWizard.launchBlurb"
                  components={[<strong key="0" />]}
                />
              </Typography>

              {launchError && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  {launchError}
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button color="light" onClick={onClose}>
            {t("modals.matchingWizard.cancel")}
          </Button>
          {step > 0 && (
            <Button color="light" onClick={() => setStep((s) => s - 1)}>
              {t("modals.matchingWizard.back")}
            </Button>
          )}
          {step < STEP_KEYS.length - 1 ? (
            <Button
              variant="contained"
              onClick={() => setStep((s) => s + 1)}
              disabled={!canProceed[step]}
            >
              {t("modals.matchingWizard.next")}
            </Button>
          ) : (
            <Button
              variant="contained"
              color="primary"
              onClick={handleLaunch}
              disabled={creatingRun || launching}
            >
              {creatingRun || launching
                ? t("modals.matchingWizard.sending")
                : t("modals.matchingWizard.sendSurvey")}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <MatchingConfigEditor
        open={configEditorOpen}
        existing={editingConfig}
        onClose={() => {
          setEditingConfig(undefined);
          setConfigEditorOpen(false);
        }}
        onSave={handleSaveConfig}
        saving={creatingConfig || updatingConfig}
      />
    </>
  );
}
